import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Package, DollarSign, PlusCircle, Search, Upload, Edit2, Trash2, Image as ImageIcon } from 'lucide-react';
import { api } from '../services/apiClient';
import ImageSelector from '../components/ImageSelector';
import { compressImage } from '../utils/imageCompressor';

const IllustrationManager = () => {
  const formRef = useRef(null);
  const [illustrations, setIllustrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', description: '', cost_price: '', markup_percent: '',
    total_stock: '', min_stock_threshold: '', category_id: '', format_id: '', image_url: '', status: 'bozza'
  });

  useEffect(() => {
    fetchIllustrations();
    fetchCategories();
    fetchFormats();
  }, []);

  useEffect(() => {
    if ((isAdding || isEditing) && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isAdding, isEditing]);

  async function fetchCategories() {
    try {
      const data = await api.categories.list();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }

  async function fetchFormats() {
    try {
      const data = await api.formats.list();
      setFormats(data);
    } catch (error) {
      console.error('Failed to fetch formats:', error);
    }
  }

  async function fetchIllustrations() {
    setLoading(true);
    try {
      const data = await api.illustrations.list();
      setIllustrations(data);
    } catch (error) {
      console.error('Failed to fetch illustrations:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      // Comprimi l'immagine
      const compressedFile = await compressImage(file, 1200, 1200, 0.85);
      const originalSizeKB = (file.size / 1024).toFixed(2);
      const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);

      // Upload self-hosted sul backend (sostituisce Cloudinary)
      const data = await api.images.upload(compressedFile);

      // Save the returned URL (/uploads/...)
      setNewItem({ ...newItem, image_url: data.url });
      if (isEditing) {
        setCurrentItem({ ...currentItem, image_url: data.url });
      }

      console.log(`✅ Immagine compressa: ${originalSizeKB}KB → ${compressedSizeKB}KB`);
    } catch (error) {
      alert(`Errore Upload: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleAddIllustration(e) {
    e.preventDefault();
    try {
      await api.illustrations.create(newItem);
      setIsAdding(false);
      setNewItem({ name: '', description: '', cost_price: '', markup_percent: '', total_stock: '', min_stock_threshold: '', category_id: '', format_id: '', image_url: '', status: 'bozza' });
      fetchIllustrations();
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function handleUpdateIllustration(e) {
    e.preventDefault();
    const updateData = {
      name: currentItem.name,
      description: currentItem.description,
      category_id: currentItem.category_id,
      format_id: currentItem.format_id || null,
      image_url: currentItem.image_url,
      cost_price: parseFloat(currentItem.cost_price),
      markup_percent: parseFloat(currentItem.markup_percent),
      total_stock: parseInt(currentItem.total_stock),
      min_stock_threshold: parseInt(currentItem.min_stock_threshold),
      status: currentItem.status,
    };
    try {
      await api.illustrations.update(currentItem.id, updateData);
      setIsEditing(false);
      setCurrentItem(null);
      fetchIllustrations();
    } catch (error) {
      alert(`Errore durante l'aggiornamento: ${error.message}`);
    }
  }

  async function handleDeleteIllustration() {
    if (!deleteConfirm) return;

    try {
      // Delete image from self-hosted storage if it exists
      const imageUrl = deleteConfirm.image_url;
      if (imageUrl && imageUrl.includes('/uploads/')) {
        try {
          const publicId = imageUrl.split('/').pop().split('.')[0];
          await api.images.delete(publicId);
        } catch (imageError) {
          console.warn('Errore durante la cancellazione dell\'immagine:', imageError);
          // Continua con la cancellazione dal DB comunque
        }
      }

      // Delete from database
      await api.illustrations.delete(deleteConfirm.id);
      setIsEditing(false);
      setCurrentItem(null);
      setDeleteConfirm(null);
      fetchIllustrations();
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (item, column) => {
    switch(column) {
      case 'name': return item.name.toLowerCase();
      case 'category': return item.categories?.name?.toLowerCase() || '';
      case 'cost_price': return Number(item.cost_price);
      case 'selling_price': return Number(item.selling_price);
      case 'total_stock': return item.total_stock;
      default: return '';
    }
  };

  const filteredItems = illustrations
    .filter(i => {
      const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === '' || i.category_id === selectedCategory;
      const matchesFormat = selectedFormat === '' || i.format_id === selectedFormat;
      return matchesSearch && matchesCategory && matchesFormat;
    })
    .sort((a, b) => {
      const aVal = getSortValue(a, sortColumn);
      const bVal = getSortValue(b, sortColumn);

      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const lowStockItems = illustrations.filter(i => i.total_stock < i.min_stock_threshold);
  const totalInventoryValue = illustrations.reduce((acc, curr) => acc + (Number(curr.cost_price) * curr.total_stock), 0);

  return (
    <div className="space-y-6">
      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl flex items-start gap-3 text-red-700">
          <AlertCircle className="shrink-0" size={20} />
          <div>
            <p className="font-bold">Attenzione: Scorte Basse!</p>
            <p className="text-sm">Ci sono {lowStockItems.length} opere che hanno superato la soglia minima di scorta.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-brand-main flex items-center gap-4 shadow-sm">
          <div className="p-3 bg-blue-100 text-blue-600 rounded-full"><Package size={24} /></div>
          <div>
            <p className="text-sm text-gray-500">Opere Totali</p>
            <p className="text-2xl font-bold">{illustrations.length}</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-brand-main flex items-center gap-4 shadow-sm">
          <div className="p-3 bg-red-100 text-red-600 rounded-full"><AlertCircle size={24} /></div>
          <div>
            <p className="text-sm text-gray-500">Scorte Basse</p>
            <p className="text-2xl font-bold">{lowStockItems.length}</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-brand-main flex items-center gap-4 shadow-sm">
          <div className="p-3 bg-green-100 text-green-600 rounded-full"><DollarSign size={24} /></div>
          <div>
            <p className="text-sm text-gray-500">Valore Magazzino</p>
            <p className="text-2xl font-bold">€ {totalInventoryValue.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Cerca opera..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => { setIsAdding(true); setIsEditing(false); }}
            className="flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all"
          >
            <PlusCircle size={18} /> Aggiungi Opera
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="flex-1 p-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main text-sm"
          >
            <option value="">Tutte le Categorie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={selectedFormat}
            onChange={(e) => setSelectedFormat(e.target.value)}
            className="flex-1 p-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main text-sm"
          >
            <option value="">Tutti i Formati</option>
            {formats.map(fmt => (
              <option key={fmt.id} value={fmt.id}>{fmt.code} — {fmt.name}</option>
            ))}
          </select>
        </div>
      </div>

      {(isAdding || isEditing) && (
        <div ref={formRef} className="bg-white p-6 rounded-xl border border-brand-main shadow-sm max-w-2xl mx-auto scroll-mt-4">
          <h3 className="text-lg font-bold mb-4 text-brand-dark">
            {isEditing ? 'Modifica Opera' : 'Nuova Opera'}
          </h3>
          <form onSubmit={isEditing ? handleUpdateIllustration : handleAddIllustration} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Nome Opera</label>
              <input required className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.name : newItem.name}
                onChange={e => isEditing ? setCurrentItem({...currentItem, name: e.target.value}) : setNewItem({...newItem, name: e.target.value})}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-3">Foto Opera</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden border flex items-center justify-center">
                  <img src={(isEditing ? currentItem.image_url : newItem.image_url) || 'https://via.placeholder.com/80'} alt="Preview" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium">
                    <Upload size={16} /> {uploading ? 'Caricamento...' : 'Carica Nuova'}
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowImageSelector(true)}
                    className="flex items-center gap-2 bg-brand-light text-brand-dark px-4 py-2 rounded-lg hover:bg-brand-main hover:text-white transition-all text-sm font-medium"
                  >
                    <ImageIcon size={16} /> Seleziona da Archivio
                  </button>
                </div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-gray-600 mb-1">Categoria</label>
              <select
                className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.category_id : newItem.category_id}
                onChange={e => isEditing ? setCurrentItem({...currentItem, category_id: e.target.value}) : setNewItem({...newItem, category_id: e.target.value})}
                required
              >
                <option value="">Seleziona Categoria...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-gray-600 mb-1">Formato</label>
              <select
                className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.format_id || '' : newItem.format_id}
                onChange={e => isEditing ? setCurrentItem({...currentItem, format_id: e.target.value || null}) : setNewItem({...newItem, format_id: e.target.value})}
              >
                <option value="">Seleziona Formato...</option>
                {formats.map(fmt => (
                  <option key={fmt.id} value={fmt.id}>{fmt.code} — {fmt.name}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Descrizione</label>
              <textarea className="w-full p-2 border rounded-md" rows="2"
                value={isEditing ? currentItem.description : newItem.description}
                onChange={e => isEditing ? setCurrentItem({...currentItem, description: e.target.value}) : setNewItem({...newItem, description: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Costo di Produzione (€)</label>
              <input required type="number" step="0.01" className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.cost_price : newItem.cost_price}
                onChange={e => isEditing ? setCurrentItem({...currentItem, cost_price: e.target.value}) : setNewItem({...newItem, cost_price: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Ricarico (%)</label>
              <input required type="number" className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.markup_percent : newItem.markup_percent}
                onChange={e => isEditing ? setCurrentItem({...currentItem, markup_percent: e.target.value}) : setNewItem({...newItem, markup_percent: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Quantità Iniziale</label>
              <input required type="number" className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.total_stock : newItem.total_stock}
                onChange={e => isEditing ? setCurrentItem({...currentItem, total_stock: e.target.value}) : setNewItem({...newItem, total_stock: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Soglia Minima Scorta</label>
              <input required type="number" className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.min_stock_threshold : newItem.min_stock_threshold}
                onChange={e => isEditing ? setCurrentItem({...currentItem, min_stock_threshold: e.target.value}) : setNewItem({...newItem, min_stock_threshold: e.target.value})}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-gray-600 mb-1">Stato</label>
              <select
                className="w-full p-2 border rounded-md"
                value={isEditing ? currentItem.status || 'bozza' : newItem.status}
                onChange={e => isEditing ? setCurrentItem({...currentItem, status: e.target.value}) : setNewItem({...newItem, status: e.target.value})}
                required
              >
                <option value="bozza">Bozza</option>
                <option value="pubblicato">Pubblicato</option>
                <option value="esaurito">Esaurito</option>
              </select>
            </div>

            <div className="col-span-2 flex justify-between gap-3 mt-4">
              <div className="flex gap-3">
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(currentItem)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Trash2 size={16} /> Cancella
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setIsAdding(false); setIsEditing(false); setCurrentItem(null); }} className="px-4 py-2 text-gray-500 hover:text-gray-700">Chiudi</button>
                <button type="submit" className="bg-brand-dark text-white px-6 py-2 rounded-lg hover:bg-opacity-90">Salva Opera</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {showImageSelector && (
        <ImageSelector
          onSelect={(imageUrl) => {
            if (isEditing) {
              setCurrentItem({ ...currentItem, image_url: imageUrl });
            } else {
              setNewItem({ ...newItem, image_url: imageUrl });
            }
            setShowImageSelector(false);
          }}
          onClose={() => setShowImageSelector(false)}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark">Cancella Opera</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler cancellare <strong>{deleteConfirm.name}</strong>? Questa azione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteIllustration}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} /> Cancella
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-brand-main overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b border-brand-main text-gray-600 text-sm uppercase">
            <tr>
              <th className="px-4 py-4 w-16">Foto</th>
              <th
                onClick={() => handleSort('name')}
                className="px-6 py-4 cursor-pointer hover:bg-gray-100 select-none"
              >
                <div className="flex items-center gap-2">
                  Opera
                  {sortColumn === 'name' && (
                    <span className="text-brand-main font-bold">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-4">Stato</th>
              <th
                onClick={() => handleSort('category')}
                className="px-6 py-4 cursor-pointer hover:bg-gray-100 select-none"
              >
                <div className="flex items-center gap-2">
                  Categoria
                  {sortColumn === 'category' && (
                    <span className="text-brand-main font-bold">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th
                onClick={() => handleSort('cost_price')}
                className="px-6 py-4 cursor-pointer hover:bg-gray-100 select-none"
              >
                <div className="flex items-center gap-2">
                  Costo/Ricarico
                  {sortColumn === 'cost_price' && (
                    <span className="text-brand-main font-bold">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th
                onClick={() => handleSort('selling_price')}
                className="px-6 py-4 cursor-pointer hover:bg-gray-100 select-none"
              >
                <div className="flex items-center gap-2">
                  Prezzo Vend.
                  {sortColumn === 'selling_price' && (
                    <span className="text-brand-main font-bold">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th
                onClick={() => handleSort('total_stock')}
                className="px-6 py-4 cursor-pointer hover:bg-gray-100 select-none"
              >
                <div className="flex items-center gap-2">
                  Scorte
                  {sortColumn === 'total_stock' && (
                    <span className="text-brand-main font-bold">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-4">Azione</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="7" className="p-8 text-center text-gray-400">Caricamento dati...</td></tr>
            ) : filteredItems.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4">
                  <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                    <img src={item.image_url || 'https://via.placeholder.com/56'} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-brand-dark">{item.name}</div>
                  <div className="text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/D'}</div>
                </td>
                <td className="px-6 py-4">
                  {item.status === 'bozza' && (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">Bozza</span>
                  )}
                  {item.status === 'pubblicato' && (
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Pubblicato</span>
                  )}
                  {item.status === 'esaurito' && (
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">Esaurito</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{item.categories?.name || 'Senza Categoria'}</td>
                <td className="px-6 py-4 text-sm">
                  <span className="text-gray-500">€{item.cost_price}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  <span className="text-brand-main font-semibold">{item.markup_percent}%</span>
                </td>
                <td className="px-6 py-4 font-bold text-brand-dark">€{item.selling_price}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${item.total_stock < item.min_stock_threshold ? 'text-red-500' : 'text-gray-600'}`}>
                      {item.total_stock} / {item.min_stock_threshold}
                    </span>
                    {item.total_stock < item.min_stock_threshold && <AlertCircle size={14} className="text-red-500" />}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setCurrentItem(item); setIsEditing(true); setIsAdding(false); }}
                      className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                      title="Modifica"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(item)}
                      className="text-red-600 hover:text-red-800 transition-colors p-1"
                      title="Cancella"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default IllustrationManager;
